import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Button,
  Center,
  Chip,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { OpportunityStatus } from '../../../models/opportunity.model';
import { OpportunityCard } from '../components/OpportunityCard';
import { useOpportunitiesQuery } from '../hooks/useOpportunitiesQuery';

const VISIBLE_STATUSES: { value: OpportunityStatus; label: string }[] = [
  { value: OpportunityStatus.Draft, label: 'Draft' },
  { value: OpportunityStatus.Proposed, label: 'Proposed' },
  { value: OpportunityStatus.Bid, label: 'Bid' },
  { value: OpportunityStatus.Live, label: 'Live' },

];

export const OpportunityBoardPage = () => {
  const navigate = useNavigate();
  const { data: opportunities, isLoading, isError } = useOpportunitiesQuery();

  const [search, setSearch] = useState('');
  const [activeStatuses, setActiveStatuses] = useState<string[]>([]);

  const filtered = useMemo(() => {
    if (!opportunities) return [];

    return opportunities.filter((o) => {
      const matchesSearch =
        search.trim() === '' ||
        o.title.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        activeStatuses.length === 0 ||
        activeStatuses.includes(String(o.opportunityStatus));

      return matchesSearch && matchesStatus;
    });
  }, [opportunities, search, activeStatuses]);

  if (isLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center h={300}>
        <Text c="red">Failed to load opportunities. Please try again.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="md" maw={1200} mx="auto" px="md" pb="xl">

      {/* ── Sticky top bar ────────────────────────────────────────────────── */}
      <Box
        style={{
          position: 'sticky',
          top: 60,
          zIndex: 100,
          backgroundColor: 'var(--mantine-color-body)',
          borderBottom: '1px solid var(--mantine-color-default-border)',
          padding: '12px 0',
          marginBottom: 8,
        }}
      >
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={3}>Talent Marketplace</Title>
            <Text c="dimmed" size="xs">Browse open opportunities and manage positions across the organisation</Text>
          </Stack>
          <Button
            leftSection={<PlusIcon style={{ width: 16, height: 16 }} />}
            onClick={() => navigate(ROUTES.OPPORTUNITY_NEW)}
          >
            New Opportunity
          </Button>
        </Group>
      </Box>

      <Stack gap="sm">
        <TextInput
          placeholder="Search by title…"
          leftSection={<MagnifyingGlassIcon style={{ width: 16, height: 16 }} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ maxWidth: 340 }}
          aria-label="Search opportunities"
        />

        <Chip.Group multiple value={activeStatuses} onChange={setActiveStatuses}>
          <Group gap="xs" wrap="wrap">
            {VISIBLE_STATUSES.map((s) => (
              <Chip key={s.value} value={String(s.value)} size="sm">
                {s.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </Stack>

      {filtered.length === 0 ? (
        <Center h={200}>
          <Stack align="center" gap="xs">
            <Text c="dimmed" size="sm">
              {opportunities?.length === 0
                ? 'No opportunities yet — create the first one'
                : 'No opportunities match your filters'}
            </Text>
            {opportunities?.length === 0 && (
              <Button
                variant="light"
                size="xs"
                onClick={() => navigate(ROUTES.OPPORTUNITY_NEW)}
              >
                Create opportunity
              </Button>
            )}
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
          {filtered.map((o) => (
            <OpportunityCard key={o.id} opportunity={o} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
};
